import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const startedRef = useRef(false);
  const [message, setMessage] = useState(
    isAr ? "جارٍ تأكيد الدفع..." : "Confirming payment..."
  );
  const [error, setError] = useState("");

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const token = params.get("token");
    const provider = (params.get("provider") || (token && !sessionId ? "paypal" : "stripe")).toLowerCase();
    const mode = params.get("mode") || "";

    if (!sessionId && !token) {
      navigate("/cart", { replace: true });
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;
    const retryDelayMs = 1500;

    async function confirmPayment() {
      try {
        setError("");
        setMessage(
          isAr ? "جارٍ التحقق من حالة الدفع..." : "Verifying payment..."
        );

        const query = new URLSearchParams();
        if (sessionId) query.set("session_id", sessionId);
        if (token) query.set("token", token);
        if (provider) query.set("provider", provider);
        if (mode) query.set("mode", mode);

        const res = await api.apiGet(`checkout/success?${query.toString()}`);

        if (cancelled) return;

        if (res?.order_id) {
          setMessage(
            isAr
              ? "تم تأكيد الدفع، جارٍ تحويلك..."
              : "Payment confirmed, redirecting..."
          );

          localStorage.removeItem("cart");
          window.dispatchEvent(new Event("cart-updated"));
          window.dispatchEvent(new Event("mg:notifications-refresh"));

          setTimeout(() => {
            if (!cancelled) {
              navigate(`/order/${res.order_id}`, { replace: true });
            }
          }, 500);

          return;
        }

        const paymentPending =
          res?.payment_status &&
          String(res.payment_status).toLowerCase() !== "paid";

        if (paymentPending && attempts < maxAttempts) {
          attempts += 1;

          setMessage(
            isAr
              ? `تم استلام جلسة الدفع، ننتظر تأكيد العملية من ${provider === "paypal" ? "PayPal" : "Stripe"}...`
              : `Payment session received, waiting for ${provider === "paypal" ? "PayPal" : "Stripe"} confirmation...`
          );

          setTimeout(() => {
            if (!cancelled) confirmPayment();
          }, retryDelayMs);

          return;
        }

        setError(
          isAr
            ? "تعذر تأكيد الدفع أو إنشاء الطلب."
            : "Unable to confirm payment or create the order."
        );

        setTimeout(() => {
          if (!cancelled) {
            navigate("/cart", { replace: true });
          }
        }, 1500);
      } catch (err) {
        if (cancelled) return;

        const status = Number(err?.status || 0);
        const body = err?.body || null;
        const backendMessage = String(
          body?.message || err?.message || ""
        ).trim();
        const lowerMessage = backendMessage.toLowerCase();

        const retryable =
          status === 400 ||
          status === 404 ||
          lowerMessage.includes("payment not completed yet") ||
          lowerMessage.includes("not completed yet") ||
          lowerMessage.includes("not found") ||
          lowerMessage.includes("expired") ||
          lowerMessage.includes("already being finalized");

        if (retryable && attempts < maxAttempts) {
          attempts += 1;

          setMessage(
            isAr
              ? "جارٍ تأكيد الدفع من مزود الخدمة..."
              : "Waiting for payment provider confirmation..."
          );

          setTimeout(() => {
            if (!cancelled) confirmPayment();
          }, retryDelayMs);

          return;
        }

        setError(
          backendMessage ||
            (isAr
              ? "حدث خطأ أثناء تأكيد الدفع."
              : "An error occurred while confirming payment.")
        );

        setTimeout(() => {
          if (!cancelled) {
            navigate("/cart", { replace: true });
          }
        }, 1800);
      }
    }

    confirmPayment();

    return () => {
      cancelled = true;
    };
  }, [navigate, isAr]);

  return (
    <div className="container py-5 text-center" dir={isAr ? "rtl" : "ltr"}>
      <div className="card shadow-sm border-0 mx-auto" style={{ maxWidth: 560 }}>
        <div className="card-body p-4">
          <h3 className="mb-3">
            {isAr ? "تأكيد عملية الدفع" : "Confirming Payment"}
          </h3>
          <p className="text-muted mb-0">{message}</p>

          {error && (
            <div className="alert alert-warning mt-3 mb-0" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
